
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { SupabaseCollection } from '@/types/supabase';
import { toast } from 'sonner';

interface UseCollectionsReturn {
  collections: CollectionWithVideos[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export interface CollectionWithVideos extends SupabaseCollection {
  firstVideoThumbnail?: string;
  videoUrlCount?: number;
}

export const useCollections = (): UseCollectionsReturn => {
  const [collections, setCollections] = useState<CollectionWithVideos[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCollections = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: collectionsData, error: collectionsError } = await supabase
        .from('collections')
        .select('*')
        .order('created_at', { ascending: false });

      if (collectionsError) {
        throw collectionsError;
      }

      // For each collection, get the first video to use its thumbnail
      const collectionsWithVideos: CollectionWithVideos[] = [];
      
      for (const collection of collectionsData) {
        const { data: videos, error: videosError } = await supabase
          .from('videos')
          .select('thumbnail, video_url')
          .eq('collection_id', collection.id)
          .limit(1);

        if (videosError) {
          console.error('Error fetching videos for collection:', videosError);
        }

        collectionsWithVideos.push({
          ...collection,
          status: collection.status as SupabaseCollection['status'],
          firstVideoThumbnail: videos && videos.length > 0 ? videos[0].thumbnail : undefined,
          videoUrlCount: videos ? videos.length : 0
        });
      }

      setCollections(collectionsWithVideos);
    } catch (err) {
      console.error('Error fetching collections:', err);
      setError(err instanceof Error ? err.message : 'Failed to load collections');
      toast.error('Failed to load collection history');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  return {
    collections,
    isLoading,
    error,
    refetch: fetchCollections
  };
};
